// @flow

import LngLat from '../geo/lng_lat';
import {makeRequest} from '../util/ajax';
import urls from '../util/urls';
import config from '../util/config';
import {getApiTokenOrKey} from '../util/util';

// https://developer.trimblemaps.com/restful-apis/location/single-search-api/
type SingleSearchOptions = {
    query: string, // required
    region?: string,
    maxResults?: number,
    currentLonLat?: LngLat,
    excludeResultsFor?: string, // excludedSearchTypes deprecated
    includeOnly?: string,
    poiCategories?: string,
    countries?: string,
    countryType?: string,
    states?: string,
    include?: string,
    useCustomPlaces?: boolean,
    separateHN?: boolean,
    getAllHNRanges?: boolean,
    includeTrimblePlaceIds?: boolean,
    lang?: string,
    matchNamedRoadsOnly?: boolean, // reverse geocoding only
    maxCleanupMiles?: number, // reverse geocoding only
    heading?: number // reverse geocoding only
}
/**
 * Search
 */
export const Search = {
    /**
     * Single Search
     *
     * @param {SingleSearchOptions} options Search options
     * @param {string} [options.query] String indicating the text to search for. This is the only <strong>required</strong> property.
     * @param {string} [options.region] Search reqgion. Defaul to NA.
     * @param {number} [options.maxResults] Limits search results by the specified number. Must be a value between 1 and 100.
     * @param {LngLat} [options.currentLonLat] The current longitude and latitude.
     * @param {string} [options.excludeResultsFor] A comma-separated list of InterpTypes to exclude from the search.
     * @param {string} [options.includeOnly] A comma-separated list of InterpTypes to include in the search response.
     * @param {string} [options.poiCategories] A comma-separated list of Points of Interest (POI) category names by which you want to filter all POI results.
     * @param {string} [options.countries] A comma-separated list of country codes by which you want to filter all results.
     * @param {string} [options.countryType] The standard for country abbreviations: ISO, FIPS, GENC2, and GENC3.
     * @param {string} [options.states] A comma-separated list of state abbreviations by which you want to filter all results.
     * @param {string} [options.include] Set to include=Meta to include additional metadata in your results, such as road grid and link information as well as the confidence level of the search results.
     * @param {boolean} [options.useCustomPlaces] If set to true, returns any custom places in the account where the location’s PlaceId or PlaceName starts with the query string.
     * @param {boolean} [options.separateHN] Sets whether the house number should be returned as a separate field from the rest of the street address. Default is false.
     * @param {boolean} [options.getAllHNRanges] If set to true, all potential house number ranges will be returned for a particular street match. Default is false.
     * @param {boolean} [options.includeTrimblePlaceIds] Set to true to return the TrimblePlaceId, PlaceName and SiteName for a location, if they exist. Default is false.
     * @param {string} [options.lang] The language to use in results. U.S. English is the default.
     * @param {boolean} [options.matchNamedRoadsOnly] When looking up coordinates, this forces reverse geocoding to match named roads only. Applicable when performing a Reverse geocode search.
     * @param {number} [options.maxCleanupMiles] When looking up coordinates, this sets a maximum radius, in miles, in which to find the closest matching road. Applicable when performing a Reverse geocode search.
     * @param {number} [options.heading] Used to determine which road link to snap to. For instance, when crossing a highway. Values are: 0 - North, 90 - East, 180 - South, 270 - East.  Applicable when performing a Reverse geocode search.
     *
     * @returns Promise<any>
     *
     * @example
     * const searchPromise = TrimbleMaps.Search.singleSearch({
     *   query: '1 independence way, princeton, nj, 08540',
     *   currentLonLat: new TrimbleMaps.LngLat(-74.600291, 40.360869),
     *   excludeResultsFor: 'POI, POItype',
     *   includeOnly: 'Country, State, County, City, POBox, Zip',
     *   poiCategories: 'Airport, bank, CAT scales',
     *   countries: 'US',
     *   countryType: 'ISO',
     *   states: 'NJ',
     *   include: 'Meta',
     *   useCustomPlaces: true,
     *   separateHN: false,
     *   getAllHNRanges: false,
     *   includeTrimblePlaceIds: true,
     *   lang: 'en-us',
     *   matchNamedRoadsOnly: true,
     *   maxCleanupMiles: 10,
     *   heading: 90
     * });
     */
    singleSearch
};

// The following function is marked private because it's documented above and having both causes in duplicate entries in the API docs.
/**
 * @private
 *
 * Single Search is our most advanced API for geocoding. It allows you to search for addresses, coordinates, places, and points of interest throughout the world.
 * @param {SingleSearchOptions} options The search options
 *
 * @returns Promise<any>
 */
function singleSearch (options: SingleSearchOptions): Promise<any> {
    const queryParams = _singleSearchQueryString(options);
    let region = options.region || 'NA';
    region = region.toUpperCase();
    const url = `${urls.SINGLESEARCH_URL}/${region}/api/search?${queryParams}`;
    return new Promise((resolve) => {
        makeRequest({
            url,
            method: 'GET',
            type: 'json',
            headers: {
                'Authorization': getApiTokenOrKey(config)
            }
        }, (_, resp: any) => {
            resolve(resp);
        });
    });
}

function _singleSearchQueryString(options: SingleSearchOptions) {
    const queryString = [];
    if (!options) {
        throw new Error(`Query is required. For example, {query: '1 independence way, princeton, nj, 08540'}`);
    }
    if (options.query !== undefined) {
        queryString.push(`query=${options.query}`);
    } else {
        throw new Error('Query string indicating the text to search for is required.');
    }
    if (options.maxResults !== undefined) {
        queryString.push(`maxResults=${options.maxResults}`);
    }
    if (options.currentLonLat !== undefined) {
        queryString.push(`currentLonLat=${options.currentLonLat.lng},${options.currentLonLat.lat}`);
    }
    if (options.excludeResultsFor !== undefined) {
        queryString.push(`excludeResultsFor=${options.excludeResultsFor}`);
    }
    if (options.includeOnly !== undefined) {
        queryString.push(`includeOnly=${options.includeOnly}`);
    }
    if (options.poiCategories !== undefined) {
        queryString.push(`poiCategories=${options.poiCategories}`);
    }
    if (options.countries !== undefined) {
        queryString.push(`countries=${options.countries}`);
    }
    if (options.countryType !== undefined) {
        queryString.push(`countryType=${options.countryType}`);
    }
    if (options.states !== undefined) {
        queryString.push(`states=${options.states}`);
    }
    if (options.include !== undefined) {
        queryString.push(`include=${options.include}`);
    }
    if (options.useCustomPlaces !== undefined) {
        queryString.push(`useCustomPlaces=${String(options.useCustomPlaces)}`);
    }
    if (options.separateHN !== undefined) {
        queryString.push(`separateHN=${String(options.separateHN)}`);
    }
    if (options.getAllHNRanges !== undefined) {
        queryString.push(`getAllHNRanges=${String(options.getAllHNRanges)}`);
    }
    if (options.includeTrimblePlaceIds !== undefined) {
        queryString.push(`includeTrimblePlaceIds=${String(options.includeTrimblePlaceIds)}`);
    }
    if (options.lang !== undefined) {
        queryString.push(`lang=${options.lang}`);
    }
    if (options.matchNamedRoadsOnly !== undefined) {
        queryString.push(`matchNamedRoadsOnly=${String(options.matchNamedRoadsOnly)}`);
    }
    if (options.maxCleanupMiles !== undefined) {
        queryString.push(`maxCleanupMiles=${options.maxCleanupMiles}`);
    }
    if (options.heading !== undefined) {
        queryString.push(`heading=${options.heading}`);
    }
    return queryString.join('&');
}
